import React, { useEffect, useMemo, useState } from "react";
import SchoolDatePicker from "../../components/SchoolDatePicker/SchoolDatePicker";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Snackbar,
  TextField,
  Typography,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import CloseIcon from "@material-ui/icons/Close";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import PageTitle from "../../components/PageTitle/PageTitle";
import HandbookService from "./Locality/Service/handbookService";
import { showAppConfirm } from '../../context/AlertContext';

const defaultDoc = {
  schoolOverview: {},
  dailySchedule: {},
  academicInformation: {},
  rulesAndConduct: {},
  uniformAndDressCode: {},
  transportation: {},
  healthAndSafety: {},
  libraryAndResources: {},
  extracurricular: {},
  parentInvolvement: {},
  feesAndFinancial: {},
  importantContacts: {},
  policiesAndAgreements: {},
};

const safeYmd = (v) => (v ? String(v).slice(0, 10) : "");
const HOURS_12 = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0"));
const MINUTES_60 = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));

function parse12h(v) {
  const m = String(v || "")
    .trim()
    .toUpperCase()
    .match(/^(0?[1-9]|1[0-2]):([0-5][0-9])\s?(AM|PM)$/);
  if (!m) return { hh: "", mm: "", meridiem: "AM" };
  return { hh: String(m[1]).padStart(2, "0"), mm: m[2], meridiem: m[3] };
}

function build12h(hh, mm, meridiem) {
  if (!hh || !mm) return "";
  return `${hh}:${mm} ${meridiem || "AM"}`;
}

function normalizeContactInput(v) {
  return String(v || "").replace(/[^\d,+\-\s]/g, "");
}

function isValidContactValue(v) {
  const raw = String(v || "").trim();
  if (!raw) return true; // optional
  const parts = raw
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  if (!parts.length) return true;
  return parts.every((p) => {
    const digits = p.replace(/\D/g, "");
    return digits.length >= 7 && digits.length <= 15;
  });
}

export default function SchoolHandbook() {
  const [doc, setDoc] = useState(defaultDoc);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [savedMsg, setSavedMsg] = useState("");
  const [snack, setSnack] = useState({ open: false, message: "" });
  const [viewOpen, setViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewDoc, setViewDoc] = useState(null);
  const [expandedViewField, setExpandedViewField] = useState("");
  const [expandedSection, setExpandedSection] = useState("schoolOverview");
  const [principalUploadName, setPrincipalUploadName] = useState("");
  const [timeParts, setTimeParts] = useState({
    arrivalTime: { hh: "", mm: "", meridiem: "AM" },
    dismissalTime: { hh: "", mm: "", meridiem: "AM" },
    saturdayArrivalTime: { hh: "", mm: "", meridiem: "AM" },
    saturdayDismissalTime: { hh: "", mm: "", meridiem: "AM" },
    libraryOpenTime: { hh: "", mm: "", meridiem: "AM" },
    libraryCloseTime: { hh: "", mm: "", meridiem: "AM" },
  });

  const sections = useMemo(
    () => [
      {
        key: "schoolOverview",
        title: "1. School Overview",
        fields: [
          { key: "visionMissionCoreValues", label: "Vision, mission, and core values", multiline: true },
          { key: "briefHistory", label: "Brief history of the school", multiline: true },
          { key: "principalMessage", label: "Message from the principal", multiline: true },
          { key: "principalImageUrl", label: "Principal photo (optional)", type: "upload" },
          { key: "yearOfEstablishment", label: "Year of establishment", type: "number" },
          { key: "branchesIfAny", label: "Branches (if any)" },
        ],
      },
      {
        key: "dailySchedule",
        title: "2. Daily Schedule",
        fields: [
          { key: "arrivalTime", label: "Arrival time (hh:mm AM/PM)", type: "text", placeholder: "08:30 AM" },
          { key: "dismissalTime", label: "Dismissal time (hh:mm AM/PM)", type: "text", placeholder: "03:15 PM" },
          { key: "breaksTime", label: "Breaks time (text or time)" },
          { key: "saturdayDifferent", label: "If Saturday is different", type: "boolean" },
          { key: "saturdayArrivalTime", label: "Saturday arrival time (hh:mm AM/PM)", type: "text", placeholder: "09:00 AM" },
          { key: "saturdayDismissalTime", label: "Saturday dismissal time (hh:mm AM/PM)", type: "text", placeholder: "12:30 PM" },
          { key: "saturdayBreaksTime", label: "Saturday breaks time (text or time)" },
          { key: "assemblyDetails", label: "Assembly details", multiline: true },
          { key: "lateArrivalEarlyLeaveProcedures", label: "Late arrival & early leave procedures", multiline: true },
        ],
      },
      {
        key: "academicInformation",
        title: "3. Academic Information",
        fields: [
          { key: "academicCalendar", label: "Academic calendar", multiline: true },
          { key: "examTerms", label: "Exam schedule (terms with dates)", type: "examTerms" },
          { key: "winterVacationFrom", label: "Winter vacation from", type: "date" },
          { key: "winterVacationTo", label: "Winter vacation to", type: "date" },
          { key: "summerVacationFrom", label: "Summer vacation from", type: "date" },
          { key: "summerVacationTo", label: "Summer vacation to", type: "date" },
        ],
      },
      {
        key: "rulesAndConduct",
        title: "4. School Rules & Code of Conduct",
        fields: [
          { key: "disciplinePoliciesConsequences", label: "Discipline policies & consequences", multiline: true },
          { key: "mobilePhonesAndDevicesPolicy", label: "Use of mobile phones & electronic devices", multiline: true },
        ],
      },
      {
        key: "uniformAndDressCode",
        title: "5. Uniform & Dress Code",
        fields: [
          { key: "dailyUniformRequirements", label: "Daily uniform requirements (if any)", multiline: true },
          { key: "specialOccasionsGuidelines", label: "Guidelines for special occasions", multiline: true },
        ],
      },
      {
        key: "transportation",
        title: "6. Transportation (if provided)",
        fields: [
          { key: "busRoutesAndRules", label: "Bus routes & rules", multiline: true },
          { key: "busStops", label: "Route stops", type: "routeStops" },
          { key: "safetyGuidelines", label: "Safety guidelines", multiline: true },
          { key: "transportContactDetails", label: "Transport contact details", multiline: true },
        ],
      },
      {
        key: "healthAndSafety",
        title: "7. Health & Safety",
        fields: [
          { key: "medicalFacilitiesFirstAid", label: "Medical facilities & first aid", multiline: true },
          { key: "emergencyProcedures", label: "Emergency procedures", multiline: true },
          { key: "illnessGuidelinesAbsence", label: "Illness guidelines & absence", multiline: true },
          { key: "vaccinationRequirements", label: "Vaccination requirements (if applicable)", multiline: true },
        ],
      },
      {
        key: "libraryAndResources",
        title: "8. Library & Learning Resources",
        fields: [
          { key: "libraryRulesAndTimings", label: "Library rules & timings", multiline: true },
          { key: "libraryOpenTime", label: "Library open time (hh:mm AM/PM)", type: "text", placeholder: "08:00 AM" },
          { key: "libraryCloseTime", label: "Library close time (hh:mm AM/PM)", type: "text", placeholder: "04:00 PM" },
          { key: "digitalResourcesUse", label: "Use of digital resources", multiline: true },
          { key: "bookBorrowingPolicies", label: "Book borrowing policies", multiline: true },
        ],
      },
      {
        key: "extracurricular",
        title: "9. Co-curricular & Extracurricular",
        fields: [
          { key: "clubsSportsCompetitions", label: "Clubs, sports and competitions", multiline: true },
          { key: "annualEventsCelebrations", label: "Annual events and celebrations", multiline: true },
        ],
      },
      {
        key: "parentInvolvement",
        title: "10. Parent Involvement",
        fields: [
          { key: "parentTeacherMeetings", label: "Parent-teacher meetings (PTMs)", multiline: true },
          { key: "communicationChannels", label: "Communication channels (diary, app, emails)", multiline: true },
        ],
      },
      {
        key: "feesAndFinancial",
        title: "11. Fees & Financial Information",
        fields: [
          { key: "feeStructurePaymentMethods", label: "Fee structure and payment methods", multiline: true },
          { key: "feeAmount", label: "Fee structure amount", type: "number", placeholder: "10000" },
          { key: "paymentScheduleDate", label: "Payment schedule date", type: "date" },
          { key: "refundPolicy", label: "Refund policy", multiline: true },
          { key: "scholarshipsConcessions", label: "Scholarships or concessions", multiline: true },
        ],
      },
      {
        key: "importantContacts",
        title: "12. Important Contacts",
        fields: [
          {
            key: "schoolOfficeContactDetails",
            label: "School office contact details",
            type: "text",
            placeholder: "e.g., 9876543210, +91 9876543211",
          },
          {
            key: "keyStaffMembers",
            label: "Key staff members",
            type: "text",
            placeholder: "e.g., 9876500001, 9876500002",
          },
          {
            key: "emergencyContacts",
            label: "Emergency contacts",
            type: "text",
            placeholder: "e.g., 100, 108, 9876543210",
          },
        ],
      },
      {
        key: "policiesAndAgreements",
        title: "13. Policies & Agreements",
        fields: [
          { key: "attendancePolicy", label: "Attendance policy", multiline: true },
          { key: "leaveApplicationProcedures", label: "Leave application procedures", multiline: true },
          { key: "internetUsagePolicy", label: "Internet usage policy", multiline: true },
          { key: "declarationFormText", label: "Declaration form text", multiline: true },
        ],
      },
    ],
    []
  );

  const getMdForField = (sectionKey, field) => {
    if (sectionKey === "dailySchedule") {
      if (field.key === "lateArrivalEarlyLeaveProcedures") return 12;
      if (field.key === "assemblyDetails") return 8;
      // Keep daily schedule items aligned in 3 columns
      return 4;
    }
    if (sectionKey === "academicInformation" && field?.type === "examTerms") {
      // Keep exam schedule aligned next to Academic calendar
      return 6;
    }
    if (
      sectionKey === "academicInformation" &&
      ["winterVacationFrom", "winterVacationTo", "summerVacationFrom", "summerVacationTo"].includes(field?.key)
    ) {
      // Keep vacation dates aligned in 2 columns (From/To)
      return 6;
    }
    if (sectionKey === "libraryAndResources" && ["libraryOpenTime", "libraryCloseTime"].includes(field?.key)) {
      // Keep library open/close time aligned as a pair
      return 6;
    }
    if (sectionKey === "libraryAndResources" && field?.key === "libraryRulesAndTimings") {
      // Keep rules text full-width so open/close time sit in one row
      return 12;
    }
    if (sectionKey === "feesAndFinancial" && ["feeAmount", "paymentScheduleDate"].includes(field?.key)) {
      // Keep amount + schedule date aligned as a pair
      return 6;
    }
    return field.multiline ? 6 : 4;
  };

  const hydrateDoc = (next) => ({
    ...defaultDoc,
    ...(next || {}),
    academicInformation: {
      ...((next || {}).academicInformation || {}),
      examTerms: Array.isArray(next?.academicInformation?.examTerms)
        ? next.academicInformation.examTerms.map((t) => ({
            title: String(t?.title || ""),
            fromDate: safeYmd(t?.fromDate),
            toDate: safeYmd(t?.toDate),
          }))
        : [
            { title: "First term / Quarterly", fromDate: "", toDate: "" },
            { title: "Second term / Half yearly", fromDate: "", toDate: "" },
            { title: "Annual", fromDate: "", toDate: "" },
          ],
      winterVacationFrom: safeYmd(next?.academicInformation?.winterVacationFrom),
      winterVacationTo: safeYmd(next?.academicInformation?.winterVacationTo),
      summerVacationFrom: safeYmd(next?.academicInformation?.summerVacationFrom),
      summerVacationTo: safeYmd(next?.academicInformation?.summerVacationTo),
    },
    feesAndFinancial: {
      ...((next || {}).feesAndFinancial || {}),
      paymentScheduleDate: safeYmd(next?.feesAndFinancial?.paymentScheduleDate),
    },
  });

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    HandbookService.getMy()
      .then((res) => {
        if (!mounted) return;
        const next = res || null;
        if (!next) {
          setDoc(defaultDoc);
          return;
        }
        setDoc(hydrateDoc(next));
      })
      .catch((e) => {
        if (!mounted) return;
        setError(e?.message || "Failed to load handbook");
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const a = parse12h(doc?.dailySchedule?.arrivalTime);
    const d = parse12h(doc?.dailySchedule?.dismissalTime);
    const sa = parse12h(doc?.dailySchedule?.saturdayArrivalTime);
    const sd = parse12h(doc?.dailySchedule?.saturdayDismissalTime);
    const lo = parse12h(doc?.libraryAndResources?.libraryOpenTime);
    const lc = parse12h(doc?.libraryAndResources?.libraryCloseTime);
    setTimeParts({
      arrivalTime: a,
      dismissalTime: d,
      saturdayArrivalTime: sa,
      saturdayDismissalTime: sd,
      libraryOpenTime: lo,
      libraryCloseTime: lc,
    });
  }, [
    doc?.dailySchedule?.arrivalTime,
    doc?.dailySchedule?.dismissalTime,
    doc?.libraryAndResources?.libraryOpenTime,
    doc?.libraryAndResources?.libraryCloseTime,
  ]);

  const setField = (sectionKey, fieldKey, value) => {
    setDoc((p) => ({
      ...p,
      [sectionKey]: {
        ...(p[sectionKey] || {}),
        [fieldKey]: value,
      },
    }));
  };

  const updateExamTerm = (index, patch) => {
    setDoc((p) => {
      const current = Array.isArray(p?.academicInformation?.examTerms)
        ? p.academicInformation.examTerms
        : [
            { title: "First term / Quarterly", fromDate: "", toDate: "" },
            { title: "Second term / Half yearly", fromDate: "", toDate: "" },
            { title: "Annual", fromDate: "", toDate: "" },
          ];
      const next = current.map((t, i) => (i === index ? { ...t, ...patch } : t));
      return {
        ...p,
        academicInformation: {
          ...(p.academicInformation || {}),
          examTerms: next,
        },
      };
    });
  };

  const addExamTerm = () => {
    setDoc((p) => ({
      ...p,
      academicInformation: {
        ...(p.academicInformation || {}),
        examTerms: [
          ...(Array.isArray(p?.academicInformation?.examTerms) ? p.academicInformation.examTerms : []),
          { title: "Other", fromDate: "", toDate: "" },
        ],
      },
    }));
  };

  const removeExamTerm = (index) => {
    setDoc((p) => {
      const current = Array.isArray(p?.academicInformation?.examTerms) ? p.academicInformation.examTerms : [];
      const next = current.filter((_, i) => i !== index);
      return {
        ...p,
        academicInformation: {
          ...(p.academicInformation || {}),
          examTerms: next,
        },
      };
    });
  };

  const updateBusStop = (index, value) => {
    setDoc((p) => {
      const current = Array.isArray(p?.transportation?.busStops) ? p.transportation.busStops : [];
      const next = current.map((item, i) => (i === index ? value : item));
      return {
        ...p,
        transportation: {
          ...(p.transportation || {}),
          busStops: next,
        },
      };
    });
  };

  const addBusStop = () => {
    setDoc((p) => ({
      ...p,
      transportation: {
        ...(p.transportation || {}),
        busStops: [...(Array.isArray(p?.transportation?.busStops) ? p.transportation.busStops : []), ""],
      },
    }));
  };

  const removeBusStop = (index) => {
    setDoc((p) => {
      const current = Array.isArray(p?.transportation?.busStops) ? p.transportation.busStops : [];
      const next = current.filter((_, i) => i !== index);
      return {
        ...p,
        transportation: {
          ...(p.transportation || {}),
          busStops: next,
        },
      };
    });
  };

  const isTwelveHourTime = (value) => {
    const v = String(value || "").trim();
    if (!v) return true; // optional field
    return /^(0?[1-9]|1[0-2]):[0-5][0-9]\s?(AM|PM)$/i.test(v);
  };

  const onSave = async () => {
    try {
      setSaving(true);
      setError("");
      setSavedMsg("");
      if (!isTwelveHourTime(doc?.dailySchedule?.arrivalTime)) {
        setError("Arrival time must be in 12-hour format (e.g., 08:30 AM).");
        return;
      }
      if (!isTwelveHourTime(doc?.dailySchedule?.dismissalTime)) {
        setError("Dismissal time must be in 12-hour format (e.g., 03:15 PM).");
        return;
      }
      if (doc?.dailySchedule?.saturdayDifferent) {
        if (!isTwelveHourTime(doc?.dailySchedule?.saturdayArrivalTime)) {
          setError("Saturday arrival time must be in 12-hour format (e.g., 09:00 AM).");
          return;
        }
        if (!isTwelveHourTime(doc?.dailySchedule?.saturdayDismissalTime)) {
          setError("Saturday dismissal time must be in 12-hour format (e.g., 12:30 PM).");
          return;
        }
      }
      if (!isTwelveHourTime(doc?.libraryAndResources?.libraryOpenTime)) {
        setError("Library open time must be in 12-hour format (e.g., 08:00 AM).");
        return;
      }
      if (!isTwelveHourTime(doc?.libraryAndResources?.libraryCloseTime)) {
        setError("Library close time must be in 12-hour format (e.g., 04:00 PM).");
        return;
      }
      if (!isValidContactValue(doc?.importantContacts?.schoolOfficeContactDetails)) {
        setError("School office contact details must be valid contact numbers (7-15 digits each).");
        return;
      }
      if (!isValidContactValue(doc?.importantContacts?.keyStaffMembers)) {
        setError("Key staff members must be valid contact numbers (7-15 digits each).");
        return;
      }
      if (!isValidContactValue(doc?.importantContacts?.emergencyContacts)) {
        setError("Emergency contacts must be valid contact numbers (7-15 digits each).");
        return;
      }
      await HandbookService.upsertMy(doc);
      // After successful save, show a fresh blank form as requested
      setDoc(defaultDoc);
      setPrincipalUploadName("");
      setExpandedSection("schoolOverview");
      setSavedMsg("Saved successfully.");
      setTimeout(() => setSavedMsg(""), 2500);
    } catch (e) {
      setError(e?.message || "Failed to save handbook");
    } finally {
      setSaving(false);
    }
  };

  const onUploadPrincipalImage = async (file) => {
    if (!file) return;
    try {
      setUploading(true);
      setError("");
      const res = await HandbookService.uploadPrincipalImage(file);
      const url = res?.url || "";
      const name = res?.imageName || file?.name || "";
      if (url) {
        setField("schoolOverview", "principalImageUrl", url);
        setPrincipalUploadName(name);
        setSnack({ open: true, message: "Principal photo uploaded successfully." });
      }
    } catch (e) {
      setError(e?.message || "Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const onRemovePrincipalImage = () => {
    setField("schoolOverview", "principalImageUrl", "");
    setPrincipalUploadName("");
    setSnack({ open: true, message: "Principal photo removed." });
  };

  const onViewAddedData = async () => {
    try {
      setViewOpen(true);
      setViewLoading(true);
      setExpandedViewField("");
      const latest = await HandbookService.getMy();
      setViewDoc(latest || null);
    } catch (e) {
      setError(e?.message || "Failed to load saved handbook");
      setViewDoc(null);
    } finally {
      setViewLoading(false);
    }
  };

  const onDeleteHandbook = async () => {
    const ok = await showAppConfirm("Are you sure you want to delete all saved handbook data?");
    if (!ok) return;
    try {
      setSaving(true);
      setError("");
      await HandbookService.deleteMy();
      setDoc(defaultDoc);
      setPrincipalUploadName("");
      setSavedMsg("Handbook deleted successfully.");
      setTimeout(() => setSavedMsg(""), 2500);
    } catch (e) {
      setError(e?.message || "Failed to delete handbook");
    } finally {
      setSaving(false);
    }
  };

  const isFilledValue = (v) => {
    if (v === null || v === undefined) return false;
    if (Array.isArray(v)) return v.some((item) => isFilledValue(item));
    if (typeof v === "string") return v.trim().length > 0;
    return true;
  };

  const getValueLabel = (value) => {
    if (Array.isArray(value)) {
      const count = value.filter((v) => isFilledValue(v)).length;
      return count ? `${count} item(s) added` : "No data";
    }
    if (typeof value === "string") return value.trim();
    return String(value || "");
  };

  const getFullValueLabel = (value) => {
    if (Array.isArray(value)) {
      return value
        .filter((v) => isFilledValue(v))
        .map((v) => (typeof v === "string" ? v : JSON.stringify(v)))
        .join(", ");
    }
    if (typeof value === "object" && value !== null) return JSON.stringify(value);
    return String(value || "");
  };

  const clearFieldValue = (sectionKey, fieldKey) => {
    if (sectionKey === "schoolOverview" && fieldKey === "principalImageUrl") {
      setPrincipalUploadName("");
    }
    if (sectionKey === "academicInformation" && fieldKey === "examTerms") {
      setField(sectionKey, fieldKey, []);
      return;
    }
    if (sectionKey === "transportation" && fieldKey === "busStops") {
      setField(sectionKey, fieldKey, []);
      return;
    }
    setField(sectionKey, fieldKey, "");
  };

  const clearFieldValueInDoc = (sourceDoc, sectionKey, fieldKey) => {
    const next = { ...(sourceDoc || {}) };
    const section = { ...(next?.[sectionKey] || {}) };

    if (sectionKey === "academicInformation" && fieldKey === "examTerms") {
      section[fieldKey] = [];
    } else if (sectionKey === "transportation" && fieldKey === "busStops") {
      section[fieldKey] = [];
    } else {
      section[fieldKey] = "";
    }

    next[sectionKey] = section;
    return next;
  };

  const onDeleteFieldFromView = async (sectionKey, fieldKey, fieldLabel) => {
    const ok = await showAppConfirm(`Are you sure you want to delete "${fieldLabel}" data?`);
    if (!ok) return;
    try {
      setSaving(true);
      setError("");
      const updated = clearFieldValueInDoc(viewDoc, sectionKey, fieldKey);
      await HandbookService.upsertMy(updated);
      const latest = await HandbookService.getMy();
      setViewDoc(latest || null);
      setSnack({ open: true, message: `"${fieldLabel}" deleted successfully.` });
    } catch (e) {
      setError(e?.message || "Failed to delete field");
    } finally {
      setSaving(false);
    }
  };

  const onEditFieldFromView = (sectionKey, fieldKey) => {
    setViewOpen(false);
    if (viewDoc) {
      setDoc(hydrateDoc(viewDoc));
    }
    setExpandedSection(sectionKey);
    setTimeout(() => {
      const el = document.getElementById(`field-${sectionKey}-${fieldKey}`);
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 120);
  };

  return (
    <>
      <PageTitle
        title="Hand Book"
        button={
          <Box display="flex" alignItems="center" gridGap={8}>
            <Button
              variant="outlined"
              style={{ borderColor: "rgb(15 59 98)", color: "rgb(15 59 98)" }}
              onClick={onViewAddedData}
              disabled={loading}
            >
              View Added Data
            </Button>
            <Button
              variant="outlined"
              style={{ borderColor: "#c62828", color: "#c62828" }}
              onClick={onDeleteHandbook}
              disabled={saving || loading}
            >
              Delete
            </Button>
            <Button
              variant="contained"
              color="secondary"
              style={{ backgroundColor: "rgb(15 59 98)" }}
              onClick={onSave}
              disabled={saving || loading}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          </Box>
        }
      />
      <Typography
        variant="body2"
        style={{
          color: "#46566b",
          marginTop: -8,
          marginBottom: 16,
          lineHeight: 1.6,
        }}
      >
        The more details you add here, the less work your staff has to do — parents get everything they need in one place.
      </Typography>

      {loading ? (
        <Box display="flex" alignItems="center" mb={2}>
          <CircularProgress size={20} style={{ marginRight: 10 }} />
          <Typography variant="body2" color="textSecondary">
            Loading handbook...
          </Typography>
        </Box>
      ) : null}

      {error ? (
        <Box mb={2}>
          <Typography variant="body2" style={{ color: "#c62828" }}>
            {error}
          </Typography>
        </Box>
      ) : null}
      {savedMsg ? (
        <Box mb={2}>
          <Typography variant="body2" style={{ color: "#2e7d32" }}>
            {savedMsg}
          </Typography>
        </Box>
      ) : null}

      <Grid container spacing={2}>
        {sections.map((s) => (
          <Grid item xs={12} key={s.key}>
            <Accordion expanded={expandedSection === s.key} onChange={(_, isExp) => setExpandedSection(isExp ? s.key : "")}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                  {s.title}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {s.fields.map((f) => (
                    <Grid id={`field-${s.key}-${f.key}`} item xs={12} md={getMdForField(s.key, f)} key={`${s.key}.${f.key}`}>
                      {s.key === "schoolOverview" && f.key === "principalImageUrl" ? (
                        <Card style={{ borderRadius: 14, border: "1px solid rgba(15,59,98,0.12)" }}>
                          <CardContent>
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                              <Box>
                                <Typography variant="subtitle2" style={{ fontWeight: 800, color: "#0f3b62" }}>
                                  Principal photo (optional)
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  Upload a clear photo (JPG/PNG). You can change it anytime.
                                </Typography>
                              </Box>
                              <Box display="flex" alignItems="center" gridGap={8}>
                                {(doc?.schoolOverview || {})?.principalImageUrl ? (
                                  <IconButton size="small" onClick={onRemovePrincipalImage} title="Remove photo">
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                ) : null}
                                <Button
                                  component="label"
                                  variant="outlined"
                                  size="small"
                                  disabled={uploading}
                                  style={{ borderColor: "rgba(15,59,98,0.35)", color: "#0f3b62" }}
                                >
                                  {uploading
                                    ? "Uploading..."
                                    : (doc?.schoolOverview || {})?.principalImageUrl
                                    ? "Change"
                                    : "Upload"}
                                  <input
                                    type="file"
                                    accept="image/*"
                                    hidden
                                    onChange={(e) => onUploadPrincipalImage(e?.target?.files?.[0])}
                                  />
                                </Button>
                              </Box>
                            </Box>

                            {(doc?.schoolOverview || {})?.principalImageUrl ? (
                              <>
                                <Box
                                  mt={1.5}
                                  style={{
                                    width: "100%",
                                    maxWidth: 360,
                                    height: 210,
                                    borderRadius: 14,
                                    overflow: "hidden",
                                    border: "1px solid rgba(15,59,98,0.15)",
                                    background: "linear-gradient(145deg, #ffffff 0%, #f6f9fd 100%)",
                                  }}
                                >
                                  <img
                                    src={(doc.schoolOverview || {}).principalImageUrl}
                                    alt="Principal"
                                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                  />
                                </Box>
                                <Box mt={1}>
                                  <Typography variant="caption" color="textSecondary">
                                    {principalUploadName ? `File: ${principalUploadName}` : "Uploaded photo"}
                                  </Typography>
                                </Box>
                              </>
                            ) : (
                              <Box mt={1.5}>
                                <Typography variant="body2" color="textSecondary">
                                  No photo uploaded yet.
                                </Typography>
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      ) : s.key === "dailySchedule" && f.key === "saturdayDifferent" ? (
                        <Card style={{ borderRadius: 14, border: "1px solid rgba(15,59,98,0.12)" }}>
                          <CardContent>
                            <Typography variant="subtitle2" style={{ fontWeight: 800, color: "#0f3b62" }}>
                              Saturday schedule
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Enable this only if Saturday timings are different from regular days.
                            </Typography>
                            <Box mt={1.2}>
                              <Button
                                variant={doc?.dailySchedule?.saturdayDifferent ? "contained" : "outlined"}
                                size="small"
                                style={{
                                  backgroundColor: doc?.dailySchedule?.saturdayDifferent ? "#0f3b62" : "transparent",
                                  color: doc?.dailySchedule?.saturdayDifferent ? "#fff" : "#0f3b62",
                                  borderColor: "rgba(15,59,98,0.35)",
                                }}
                                onClick={() => {
                                  const next = !doc?.dailySchedule?.saturdayDifferent;
                                  setField("dailySchedule", "saturdayDifferent", next);
                                  if (!next) {
                                    setField("dailySchedule", "saturdayArrivalTime", "");
                                    setField("dailySchedule", "saturdayDismissalTime", "");
                                    setField("dailySchedule", "saturdayBreaksTime", "");
                                    setTimeParts((p) => ({
                                      ...p,
                                      saturdayArrivalTime: { hh: "", mm: "", meridiem: "AM" },
                                      saturdayDismissalTime: { hh: "", mm: "", meridiem: "AM" },
                                    }));
                                  }
                                }}
                              >
                                {doc?.dailySchedule?.saturdayDifferent ? "Saturday is different" : "Saturday same as regular days"}
                              </Button>
                            </Box>
                          </CardContent>
                        </Card>
                      ) : s.key === "dailySchedule" &&
                        (f.key === "arrivalTime" ||
                          f.key === "dismissalTime" ||
                          f.key === "saturdayArrivalTime" ||
                          f.key === "saturdayDismissalTime") ? (
                        <>
                          <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>
                            {f.label}
                          </Typography>
                          {f.key.startsWith("saturday") && !doc?.dailySchedule?.saturdayDifferent ? (
                            <Typography variant="caption" color="textSecondary">
                              Enable “Saturday is different” to set Saturday timings.
                            </Typography>
                          ) : (
                          <Box display="flex" gridGap={8}>
                            <TextField
                              select
                              label="Hour"
                              value={timeParts[f.key]?.hh || ""}
                              disabled={f.key.startsWith("saturday") && !doc?.dailySchedule?.saturdayDifferent}
                              onChange={(e) => {
                                const next = {
                                  ...(timeParts[f.key] || { hh: "", mm: "", meridiem: "AM" }),
                                  hh: e.target.value,
                                };
                                setTimeParts((p) => ({ ...p, [f.key]: next }));
                                if (next.hh && next.mm) {
                                  setField(s.key, f.key, build12h(next.hh, next.mm, next.meridiem));
                                }
                              }}
                              variant="outlined"
                              fullWidth
                            >
                              {HOURS_12.map((h) => (
                                <MenuItem key={h} value={h}>
                                  {h}
                                </MenuItem>
                              ))}
                            </TextField>
                            <TextField
                              select
                              label="Minute"
                              value={timeParts[f.key]?.mm || ""}
                              disabled={f.key.startsWith("saturday") && !doc?.dailySchedule?.saturdayDifferent}
                              onChange={(e) => {
                                const next = {
                                  ...(timeParts[f.key] || { hh: "", mm: "", meridiem: "AM" }),
                                  mm: e.target.value,
                                };
                                setTimeParts((p) => ({ ...p, [f.key]: next }));
                                if (next.hh && next.mm) {
                                  setField(s.key, f.key, build12h(next.hh, next.mm, next.meridiem));
                                }
                              }}
                              variant="outlined"
                              fullWidth
                            >
                              {MINUTES_60.map((m) => (
                                <MenuItem key={m} value={m}>
                                  {m}
                                </MenuItem>
                              ))}
                            </TextField>
                            <TextField
                              select
                              label="AM/PM"
                              value={timeParts[f.key]?.meridiem || "AM"}
                              disabled={f.key.startsWith("saturday") && !doc?.dailySchedule?.saturdayDifferent}
                              onChange={(e) => {
                                const next = {
                                  ...(timeParts[f.key] || { hh: "", mm: "", meridiem: "AM" }),
                                  meridiem: e.target.value,
                                };
                                setTimeParts((p) => ({ ...p, [f.key]: next }));
                                if (next.hh && next.mm) {
                                  setField(s.key, f.key, build12h(next.hh, next.mm, next.meridiem));
                                }
                              }}
                              variant="outlined"
                              fullWidth
                            >
                              <MenuItem value="AM">AM</MenuItem>
                              <MenuItem value="PM">PM</MenuItem>
                            </TextField>
                          </Box>
                          )}
                          <Typography variant="caption" color="textSecondary">
                            Selected:{" "}
                            {timeParts[f.key]?.hh || timeParts[f.key]?.mm
                              ? `${timeParts[f.key]?.hh || "--"}:${timeParts[f.key]?.mm || "--"} ${
                                  timeParts[f.key]?.meridiem || "AM"
                                }`
                              : "Not set"}
                          </Typography>
                        </>
                      ) : s.key === "libraryAndResources" && (f.key === "libraryOpenTime" || f.key === "libraryCloseTime") ? (
                        <>
                          <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>
                            {f.label}
                          </Typography>
                          <Box display="flex" gridGap={8}>
                            <TextField
                              select
                              label="Hour"
                              value={timeParts[f.key]?.hh || ""}
                              onChange={(e) => {
                                const next = {
                                  ...(timeParts[f.key] || { hh: "", mm: "", meridiem: "AM" }),
                                  hh: e.target.value,
                                };
                                setTimeParts((p) => ({ ...p, [f.key]: next }));
                                if (next.hh && next.mm) {
                                  setField(s.key, f.key, build12h(next.hh, next.mm, next.meridiem));
                                }
                              }}
                              variant="outlined"
                              fullWidth
                            >
                              {HOURS_12.map((h) => (
                                <MenuItem key={h} value={h}>
                                  {h}
                                </MenuItem>
                              ))}
                            </TextField>
                            <TextField
                              select
                              label="Minute"
                              value={timeParts[f.key]?.mm || ""}
                              onChange={(e) => {
                                const next = {
                                  ...(timeParts[f.key] || { hh: "", mm: "", meridiem: "AM" }),
                                  mm: e.target.value,
                                };
                                setTimeParts((p) => ({ ...p, [f.key]: next }));
                                if (next.hh && next.mm) {
                                  setField(s.key, f.key, build12h(next.hh, next.mm, next.meridiem));
                                }
                              }}
                              variant="outlined"
                              fullWidth
                            >
                              {MINUTES_60.map((m) => (
                                <MenuItem key={m} value={m}>
                                  {m}
                                </MenuItem>
                              ))}
                            </TextField>
                            <TextField
                              select
                              label="AM/PM"
                              value={timeParts[f.key]?.meridiem || "AM"}
                              onChange={(e) => {
                                const next = {
                                  ...(timeParts[f.key] || { hh: "", mm: "", meridiem: "AM" }),
                                  meridiem: e.target.value,
                                };
                                setTimeParts((p) => ({ ...p, [f.key]: next }));
                                if (next.hh && next.mm) {
                                  setField(s.key, f.key, build12h(next.hh, next.mm, next.meridiem));
                                }
                              }}
                              variant="outlined"
                              fullWidth
                            >
                              <MenuItem value="AM">AM</MenuItem>
                              <MenuItem value="PM">PM</MenuItem>
                            </TextField>
                          </Box>
                          <Typography variant="caption" color="textSecondary">
                            Selected:{" "}
                            {timeParts[f.key]?.hh || timeParts[f.key]?.mm
                              ? `${timeParts[f.key]?.hh || "--"}:${timeParts[f.key]?.mm || "--"} ${
                                  timeParts[f.key]?.meridiem || "AM"
                                }`
                              : "Not set"}
                          </Typography>
                        </>
                      ) : s.key === "academicInformation" && f.type === "examTerms" ? (
                        <Card
                          style={{
                            borderRadius: 14,
                            border: "1px solid rgba(15,59,98,0.12)",
                            height: "100%",
                          }}
                        >
                          <CardContent style={{ height: "100%", display: "flex", flexDirection: "column" }}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                              <Box>
                                <Typography variant="subtitle2" style={{ fontWeight: 800, color: "#0f3b62" }}>
                                  Exam schedule
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  Add term/quarterly/annual schedules with from-to dates.
                                </Typography>
                              </Box>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={addExamTerm}
                                style={{ borderColor: "rgba(15,59,98,0.35)", color: "#0f3b62" }}
                              >
                                Add schedule
                              </Button>
                            </Box>

                            <Grid container spacing={2} style={{ flex: 1 }}>
                              {(Array.isArray(doc?.academicInformation?.examTerms)
                                ? doc.academicInformation.examTerms
                                : []).map((t, idx) => (
                                <Grid item xs={12} key={`examTerm.${idx}`}>
                                  <Card style={{ borderRadius: 12, border: "1px solid rgba(15,59,98,0.10)" }}>
                                    <CardContent>
                                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                        <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                                          {t?.title || `Schedule ${idx + 1}`}
                                        </Typography>
                                        {idx >= 3 ? (
                                          <IconButton size="small" onClick={() => removeExamTerm(idx)} title="Remove">
                                            <DeleteOutlineIcon fontSize="small" />
                                          </IconButton>
                                        ) : null}
                                      </Box>
                                      <Grid container spacing={2}>
                                        <Grid item xs={12} md={4}>
                                          <TextField
                                            label="Name"
                                            value={t?.title || ""}
                                            onChange={(e) => updateExamTerm(idx, { title: e.target.value })}
                                            fullWidth
                                            variant="outlined"
                                          />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                          <SchoolDatePicker
                                            label="From"
                                            value={t?.fromDate || ""}
                                            onChange={(iso) => updateExamTerm(idx, { fromDate: iso })}
                                          />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                          <SchoolDatePicker
                                            label="To"
                                            value={t?.toDate || ""}
                                            onChange={(iso) => updateExamTerm(idx, { toDate: iso })}
                                          />
                                        </Grid>
                                      </Grid>
                                    </CardContent>
                                  </Card>
                                </Grid>
                              ))}
                            </Grid>
                          </CardContent>
                        </Card>
                      ) : s.key === "transportation" && f.type === "routeStops" ? (
                        <Card style={{ borderRadius: 14, border: "1px solid rgba(15,59,98,0.12)" }}>
                          <CardContent>
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                              <Box>
                                <Typography variant="subtitle2" style={{ fontWeight: 800, color: "#0f3b62" }}>
                                  Route stops
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  Add all bus stops in order.
                                </Typography>
                              </Box>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={addBusStop}
                                style={{ borderColor: "rgba(15,59,98,0.35)", color: "#0f3b62" }}
                              >
                                Add stop
                              </Button>
                            </Box>

                            {Array.isArray(doc?.transportation?.busStops) && doc.transportation.busStops.length > 0 ? (
                              <Grid container spacing={1}>
                                {doc.transportation.busStops.map((stop, idx) => (
                                  <Grid item xs={12} key={`bus-stop-${idx}`}>
                                    <Box display="flex" alignItems="center" gridGap={8}>
                                      <TextField
                                        label={`Stop ${idx + 1}`}
                                        value={stop || ""}
                                        onChange={(e) => updateBusStop(idx, e.target.value)}
                                        fullWidth
                                        variant="outlined"
                                      />
                                      <IconButton size="small" onClick={() => removeBusStop(idx)} title="Remove stop">
                                        <DeleteOutlineIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  </Grid>
                                ))}
                              </Grid>
                            ) : (
                              <Typography variant="body2" color="textSecondary">
                                No stops added yet.
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      ) : f.type === "date" ? (
                        <SchoolDatePicker
                          label={f.label}
                          value={(doc?.[s.key] || {})[f.key] || ""}
                          onChange={(iso) => setField(s.key, f.key, iso)}
                        />
                      ) : (
                        <TextField
                          label={f.label}
                          type={f.type}
                          value={(doc?.[s.key] || {})[f.key] || ""}
                          onChange={(e) => {
                            const raw = e.target.value;
                            if (s.key === "schoolOverview" && f.key === "yearOfEstablishment") {
                              // Numeric only
                              const digits = String(raw || "").replace(/\D/g, "").slice(0, 4);
                              setField(s.key, f.key, digits);
                              return;
                            }
                            if (s.key === "feesAndFinancial" && f.key === "feeAmount") {
                              const digits = String(raw || "").replace(/\D/g, "");
                              setField(s.key, f.key, digits);
                              return;
                            }
                            if (
                              s.key === "importantContacts" &&
                              ["schoolOfficeContactDetails", "keyStaffMembers", "emergencyContacts"].includes(f.key)
                            ) {
                              setField(s.key, f.key, normalizeContactInput(raw));
                              return;
                            }
                            setField(s.key, f.key, raw);
                          }}
                          fullWidth
                          variant="outlined"
                          multiline={!!f.multiline}
                          rows={f.multiline ? 4 : undefined}
                          InputLabelProps={f.type === "date" || f.type === "time" ? { shrink: true } : undefined}
                          placeholder={f.placeholder}
                          inputProps={
                            s.key === "schoolOverview" && f.key === "yearOfEstablishment"
                              ? { inputMode: "numeric", pattern: "[0-9]*", min: 1800, max: 2100 }
                              : s.key === "importantContacts" &&
                                ["schoolOfficeContactDetails", "keyStaffMembers", "emergencyContacts"].includes(f.key)
                              ? { inputMode: "numeric" }
                              : undefined
                          }
                        />
                      )}
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>
        ))}
      </Grid>

      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>View Added Data</DialogTitle>
        <DialogContent dividers>
          {viewLoading ? (
            <Box display="flex" alignItems="center">
              <CircularProgress size={18} style={{ marginRight: 10 }} />
              <Typography variant="body2" color="textSecondary">
                Loading saved data...
              </Typography>
            </Box>
          ) : !viewDoc ? (
            <Typography variant="body2" color="textSecondary">
              No saved handbook data found.
            </Typography>
          ) : (
            sections.map((s) => {
              const rows = s.fields
                .map((f) => ({ field: f, value: (viewDoc?.[s.key] || {})[f.key] }))
                .filter((x) => isFilledValue(x.value));
              if (!rows.length) return null;
              return (
                <Box key={`view-${s.key}`} mb={2}>
                  <Typography variant="subtitle1" style={{ fontWeight: 700, marginBottom: 8, color: "#0f3b62" }}>
                    {s.title}
                  </Typography>
                  {rows.map(({ field, value }) => {
                    const rowId = `${s.key}.${field.key}`;
                    const isExpanded = expandedViewField === rowId;
                    return (
                      <Box
                        key={`view-${s.key}-${field.key}`}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        style={{
                          border: "1px solid rgba(15,59,98,0.12)",
                          borderRadius: 10,
                          padding: "10px 12px",
                          marginBottom: 8,
                        }}
                      >
                        <Box
                          mr={2}
                          style={{ minWidth: 0, cursor: "pointer", flex: 1 }}
                          onClick={() => setExpandedViewField(isExpanded ? "" : rowId)}
                          title="Click to view full value"
                        >
                          <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                            {field.label}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            noWrap={!isExpanded}
                            style={{ whiteSpace: isExpanded ? "pre-wrap" : "nowrap" }}
                            title={!isExpanded ? getValueLabel(value) : ""}
                          >
                            {isExpanded ? getFullValueLabel(value) : getValueLabel(value)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {isExpanded ? "Click again to collapse" : "Click to view full data"}
                          </Typography>
                        </Box>
                        <Box display="flex" gridGap={8}>
                          <Button size="small" variant="outlined" onClick={() => onEditFieldFromView(s.key, field.key)}>
                            Edit
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            style={{ color: "#c62828", borderColor: "#c62828" }}
                            onClick={() => onDeleteFieldFromView(s.key, field.key, field.label)}
                            disabled={saving}
                          >
                            Delete
                          </Button>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              );
            })
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        open={snack.open}
        autoHideDuration={2500}
        onClose={() => setSnack({ open: false, message: "" })}
        message={snack.message}
        action={
          <IconButton size="small" aria-label="close" color="inherit" onClick={() => setSnack({ open: false, message: "" })}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </>
  );
}

